<template>
  <div id="app">
    <PWAInstallPrompt @show-ios-instructions="showIOSInstructions" />
    <header>
      <nav>
        <router-link class="header-router" to="/">
          <div class="logo">
            <Trophy :size="24" />
            <span class="header-text">CFB Scores</span>
          </div>
        </router-link>
        <div class="nav-section">
          <div class="nav-links">
            <router-link to="/">Scores</router-link>
            <router-link to="/teams">Teams</router-link>
            <router-link to="/conferences">Conferences</router-link>
            <router-link to="/settings" class="settings-icon">
              <SettingsIcon :size="16" />
            </router-link>
          </div>
          <div class="last-updated-container">
            <div
              class="last-updated"
              v-if="lastUpdated"
              :title="this.lastUpdated"
            >
              <Clock class="clock" :size="12" />
              Updated {{ timeAgo }}
            </div>
            <button
              class="refresh-btn"
              @click="forceRefresh"
              :disabled="isRefreshing"
              :title="isRefreshing ? 'Refreshing...' : 'Force refresh data'"
            >
              <RefreshCw
                class="refresh-icon"
                :size="14"
                :class="{ spinning: isRefreshing }"
              />
            </button>
          </div>
        </div>
        <!-- Mobile last updated -->
        <div class="mobile-last-updated" v-if="lastUpdated">
          <button
            class="refresh-btn"
            @click="forceRefresh"
            :disabled="isRefreshing"
            :title="isRefreshing ? 'Refreshing...' : 'Force refresh data'"
          >
            <RefreshCw
              class="refresh-icon"
              :size="14"
              :class="{ spinning: isRefreshing }"
            />
          </button>
          <Clock class="clock" :size="10" />
          {{ timeAgo }}
        </div>
      </nav>
    </header>

    <!-- Mobile bottom navigation -->
    <nav class="mobile-bottom-nav">
      <router-link to="/">
        <div class="icon-container">
          <Home :size="24" />
        </div>
        <span>Scores</span>
      </router-link>
      <router-link to="/teams">
        <div class="icon-container">
          <Users :size="24" />
        </div>
        <span>Teams</span>
      </router-link>
      <router-link to="/conferences">
        <div class="icon-container">
          <Network :size="24" />
        </div>
        <span>Conferences</span>
      </router-link>
      <router-link to="/settings">
        <div class="icon-container">
          <SettingsIcon :size="24" />
        </div>
        <span>Settings</span>
      </router-link>
    </nav>

    <main>
      <router-view />
    </main>
  </div>
</template>

<script>
import { getLastUpdateTime, refreshGameData } from "./utils/localDataLoader";
import PWAInstallPrompt from "./components/PWAInstallPrompt.vue";
import {
  Home,
  Users,
  Network,
  Settings as SettingsIcon,
  Trophy,
  Clock,
  RefreshCw,
} from "lucide-vue-next";

export default {
  name: "App",
  components: {
    PWAInstallPrompt,
    Home,
    Users,
    Network,
    SettingsIcon,
    Trophy,
    Clock,
    RefreshCw,
  },
  data() {
    return {
      lastUpdated: null,
      isRefreshing: false,
    };
  },
  computed: {
    timeAgo() {
      if (!this.lastUpdated) return "Unknown";

      const now = new Date();
      const updated = new Date(this.lastUpdated);
      const seconds = Math.floor((now - updated) / 1000);

      // Less than a minute
      if (seconds < 60) {
        return "just now";
      }

      // Minutes
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) {
        return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
      }

      // Hours
      const hours = Math.floor(minutes / 60);
      if (hours < 24) {
        return `${hours} hour${hours > 1 ? "s" : ""} ago`;
      }

      // Days
      const days = Math.floor(hours / 24);
      if (days < 7) {
        return `${days} day${days > 1 ? "s" : ""} ago`;
      }

      // Weeks
      const weeks = Math.floor(days / 7);
      if (weeks < 4) {
        return `${weeks} week${weeks > 1 ? "s" : ""} ago`;
      }

      // Fall back to formatted date for older updates
      return updated.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    },
  },
  async created() {
    // Load last update time when app starts
    this.lastUpdated = await getLastUpdateTime();
  },
  methods: {
    async forceRefresh() {
      if (this.isRefreshing) return;

      this.isRefreshing = true;
      try {
        await refreshGameData();
        this.lastUpdated = await getLastUpdateTime();
      } catch (error) {
        console.error("Failed to refresh data:", error);
      } finally {
        this.isRefreshing = false;
      }
    },

    showIOSInstructions() {
      alert(
        "To install CFB Scores on your iOS device:\n\n" +
          "1. Tap the Share button (square with arrow) at the bottom of Safari\n" +
          "2. Scroll down and tap 'Add to Home Screen'\n" +
          "3. Tap 'Add' to confirm\n\n" +
          "This will add the app to your home screen for easy access!"
      );
    },
  },
};
</script>

<style>
#app {
  font-family: "Roboto", sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #e0e0e0;
  background-color: #0d1117;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

header {
  background-color: #161b22;
  color: #c9d1d9;
  padding: 2rem 0;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid #30363d;
  height: 60px;
}

nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  height: 100%;
}

.nav-section {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.header-router {
  color: white;
  text-decoration: none;
  margin-left: 12px;
  font-weight: 500;
  transition: color 0.3s;
}

.logo {
  display: flex;
  align-items: center;
  font-size: 1.5rem;
  font-weight: 700;
  .header-text {
    margin-inline-start: 8px;
  }
}

.logo i {
  margin-right: 0.5rem;
  color: #ff5722;
}

.nav-links {
  display: flex;
  align-items: center;
}

.nav-links a {
  color: white;
  text-decoration: none;
  margin-left: 12px;
  font-weight: 500;
  transition: color 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-links a:hover,
.nav-links a.router-link-active {
  color: #ff7b45;
}

.nav-links a.settings-icon {
  margin-left: 12px;
  padding: 0.375rem;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.nav-links a.settings-icon:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.last-updated-container {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
}

.last-updated {
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  white-space: nowrap;
  color: #8b949e;
}

.last-updated .clock {
  color: #8b949e;
  margin: 0 6px 0 0;
  flex-shrink: 0;
}

.refresh-btn {
  background: none;
  border: none;
  color: #8b949e;
  cursor: pointer;
  padding: 0.375rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.refresh-btn:hover {
  color: #c9d1d9;
  background-color: rgba(255, 255, 255, 0.1);
}

.refresh-btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.refresh-icon {
  transition: transform 0.3s ease;
}

.refresh-icon.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

main {
  flex: 1;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 2rem 1rem;
}

footer {
  background-color: #161b22;
  color: #8b949e;
  text-align: center;
  padding: 1rem 0;
  border-top: 1px solid #30363d;
}

@media (max-width: 768px) {
  header {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    height: 60px;
  }

  nav {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 1rem;
    gap: 1rem;
    height: 100%;
  }

  .nav-section {
    display: none;
  }

  .logo {
    font-size: 1.2rem;
    flex: 1;
  }

  .mobile-last-updated {
    display: flex !important;
    align-items: center;
    font-size: 0.65rem;
    color: #8b949e;
    white-space: nowrap;
    gap: 0.25rem;
    .clock {
      color: #8b949e;
    }
  }

  /* Mobile bottom navigation */
  .mobile-bottom-nav {
    display: flex !important;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: #161b22;
    border-top: 1px solid #30363d;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.3);
    z-index: 1000;
    flex-direction: row !important;
    justify-content: space-around;
    align-items: stretch;
    height: 60px;
  }

  .mobile-bottom-nav a {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0.5rem 0.2rem;
    color: #c9d1d9;
    text-decoration: none;
    font-size: 0.65rem;
    font-weight: 500;
    transition: color 0.3s;
    gap: 0.25rem;
    text-align: center;
    white-space: nowrap;
    min-height: 100%;
  }

  .mobile-bottom-nav a:hover,
  .mobile-bottom-nav a.router-link-active {
    color: #ff7b45;
  }

  .mobile-bottom-nav svg {
    width: 24px;
    height: 24px;
    stroke: currentColor;
    fill: none;
    display: block;
    margin: 0 auto;
  }

  .mobile-bottom-nav .icon-container {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    margin: 0 auto 4px auto;
  }

  main {
    margin-top: 60px;
    margin-bottom: 60px;
    padding: 1rem 0.5rem;
    min-height: calc(100vh - 120px);
  }
}

@media (min-width: 769px) {
  .mobile-bottom-nav {
    display: none;
  }

  .mobile-last-updated {
    display: none !important;
  }
}
</style>
